import { useEffect, useState } from "react";
import { Link, NavLink, Route, Routes } from "react-router-dom";
import { BarChart3, Film, BookOpen, FileText, CalendarDays, FolderDown, HeartHandshake, LayoutTemplate, Quote, Tags, Users, CreditCard, Settings, Mail, Inbox, GraduationCap, ArrowLeft, Menu, ShoppingBag } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import CollectionManager from "@/pages/admin/CollectionManager";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";

const NAV = [
  ["", "Analytics", BarChart3], ["videos", "Videos", Film], ["categories", "Categories", Tags], ["courses", "Courses", BookOpen],
  ["posts", "Blog", FileText], ["events", "Events", CalendarDays], ["resources", "Resources", FolderDown], ["products", "Store", ShoppingBag], ["programs", "Coaching", HeartHandshake],
  ["landing_pages", "Landing pages", LayoutTemplate], ["testimonials", "Testimonials", Quote], ["enrollments", "Enrollments", GraduationCap],
  ["messages", "Messages", Inbox], ["subscribers", "Subscribers", Mail],
  ["members", "Members", Users, true], ["transactions", "Payments", CreditCard, true], ["pricing", "Pricing", CreditCard, true], ["site", "Site & SEO", Settings, true],
];

const Stat = ({ label, value, testid }) => (
  <div className="card-lux p-5" data-testid={testid}><p className="text-xs uppercase tracking-widest text-slate-500">{label}</p><p className="font-serif text-3xl font-semibold text-white mt-2">{value}</p></div>
);

const Overview = () => {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)); }, []);
  if (!a) return null;
  return (
    <div className="space-y-8" data-testid="admin-analytics">
      <h1 className="font-serif text-3xl font-semibold text-white">Platform analytics</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total accounts" value={a.members} testid="stat-members" />
        <Stat label="Active subscriptions" value={a.active_subscriptions} testid="stat-subscriptions" />
        <Stat label="Enrollments" value={a.enrollments} testid="stat-enrollments" />
        <Stat label="Revenue (paid)" value={`$${a.revenue}`} testid="stat-revenue" />
        <Stat label="Free members" value={a.tiers.free} />
        <Stat label="Premium members" value={a.tiers.premium} />
        <Stat label="Coaching members" value={a.tiers.coaching} />
        <Stat label="Newsletter subscribers" value={a.subscribers} />
      </div>
      <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
        <div className="card-lux p-5 h-72">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-3">Page views, last 30 days (consented visits)</p>
          <ResponsiveContainer width="100%" height="85%">
            <AreaChart data={a.daily_views}><XAxis dataKey="day" stroke="#64748b" fontSize={11} /><YAxis stroke="#64748b" fontSize={11} allowDecimals={false} /><Tooltip contentStyle={{ background: "#0A0F1D", border: "1px solid #333" }} /><Area dataKey="views" stroke="#D4AF37" fill="#D4AF37" fillOpacity={0.2} /></AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="card-lux p-5">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-3">Top pages</p>
          {a.top_pages.map((p) => <div key={p.path} className="flex justify-between text-sm py-1.5 text-slate-300"><span className="truncate">{p.path}</span><span>{p.views}</span></div>)}
          {!a.top_pages.length && <p className="text-sm text-slate-500">No data yet.</p>}
        </div>
      </div>
    </div>
  );
};

const Table = ({ title, cols, rows, testid }) => (
  <div data-testid={testid}>
    <h1 className="font-serif text-3xl font-semibold text-white mb-6">{title}</h1>
    <div className="overflow-x-auto rounded-xl border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-slate-400"><tr>{cols.map(([, h]) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => <tr key={r.id || i} className="border-t border-white/5 text-slate-200">{cols.map(([k, h, fn]) => <td key={h} className="p-3 align-top">{fn ? fn(r) : String(r[k] ?? "")}</td>)}</tr>)}
          {!rows.length && <tr><td colSpan={cols.length} className="p-10 text-center text-slate-500">Nothing yet.</td></tr>}
        </tbody>
      </table>
    </div>
  </div>
);

const useFetch = (path) => {
  const [d, setD] = useState([]);
  const load = () => api.get(path).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  return [d, load];
};

const Members = () => {
  const { user: me } = useAuth();
  const [rows, load] = useFetch("/admin/users");
  const update = async (u, patch) => {
    try { await api.put(`/admin/users/${u.id}`, { role: u.role, tier: u.tier, ...patch }); toast.success("Member updated"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (u) => { if (window.confirm(`Delete ${u.email}?`)) { try { await api.delete(`/admin/users/${u.id}`); load(); } catch (e) { toast.error(errMsg(e)); } } };
  const sel = (u, k, opts) => (
    <select value={u[k]} onChange={(e) => update(u, { [k]: e.target.value })} className="field !py-1.5 !w-32" data-testid={`member-${k}-${u.email}`}>{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select>
  );
  return <Table title="Members" testid="admin-members" rows={rows} cols={[["name", "Name"], ["email", "Email"], ["role", "Role", (u) => sel(u, "role", ["member", "editor", "admin"])], ["tier", "Membership", (u) => sel(u, "tier", ["free", "premium", "coaching"])],
    ["subscription_status", "Subscription"], ["created_at", "Joined", (u) => fmtDate(u.created_at)], ["x", "", (u) => u.id !== me.id && <button onClick={() => del(u)} className="text-xs text-red-400" data-testid={`member-delete-${u.email}`}>Delete</button>]]} />;
};

const ReplyCell = ({ m, load }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  if (m.type !== "coaching_question") return null;
  const send = async () => {
    setBusy(true);
    try { await api.put(`/admin/messages/${m.id}/reply`, { reply: text }); toast.success("Reply sent"); setOpen(false); setText(""); load(); window.dispatchEvent(new Event("coaching-updated")); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-3 max-w-md" data-testid={`admin-reply-${m.id}`}>
      {m.reply && <div className="border-l-2 border-[#D4AF37] pl-3 text-xs text-slate-300 whitespace-pre-line mb-2" data-testid={`admin-reply-text-${m.id}`}><span className="text-gold-light">Replied {fmtDate(m.replied_at)}:</span> {m.reply}</div>}
      {open ? (
        <div className="space-y-2">
          <textarea className="field min-h-[110px] text-sm" value={text} onChange={(e) => setText(e.target.value)} placeholder="Write your answer and any resources..." data-testid={`admin-reply-input-${m.id}`} />
          <div className="flex gap-2">
            <button disabled={busy || text.trim().length < 2} onClick={send} className="btn-gold !py-1.5 !px-4 !text-xs" data-testid={`admin-reply-send-${m.id}`}>{busy ? "Sending..." : "Send reply"}</button>
            <button onClick={() => setOpen(false)} className="text-xs text-slate-400" data-testid={`admin-reply-cancel-${m.id}`}>Cancel</button>
          </div>
        </div>
      ) : <button onClick={() => setOpen(true)} className="text-xs text-gold-light underline" data-testid={`admin-reply-open-${m.id}`}>{m.reply ? "Send another reply" : "Reply to member"}</button>}
    </div>
  );
};

const Messages = () => {
  const [rows, load] = useFetch("/admin/messages");
  return <Table title="Contact messages" testid="admin-messages" rows={rows} cols={[["created_at", "Date", (m) => fmtDate(m.created_at)], ["name", "From", (m) => <>{m.name}<br /><a href={`mailto:${m.email}`} className="text-gold-light text-xs">{m.email}</a></>], ["subject", "Subject"], ["message", "Message", (m) => <><p className="max-w-md whitespace-pre-line">{m.message}</p><ReplyCell m={m} load={load} /></>],
    ["read", "", (m) => (m.read ? <span className="text-xs text-slate-500">Read</span> : <button onClick={() => api.put(`/admin/messages/${m.id}/read`).then(load)} className="text-xs text-gold-light">Mark read</button>)]]} />;
};

const Simple = ({ path, title, cols }) => {
  const [rows] = useFetch(path);
  return <Table title={title} rows={rows} cols={cols} testid={`admin-${title.toLowerCase()}`} />;
};

const useUnanswered = () => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const load = () => { api.get("/admin/messages/unanswered-count").then((r) => setN(r.data.count)).catch(() => {}); };
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("coaching-updated", load);
    return () => { clearInterval(t); window.removeEventListener("coaching-updated", load); };
  }, []);
  return n;
};

export default function Admin() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const isAdmin = user.role === "admin";
  const unanswered = useUnanswered();
  return (
    <div className="min-h-screen bg-black lg:flex" data-testid="admin-page">
      <button onClick={() => setOpen(!open)} className="lg:hidden fixed top-4 right-4 z-50 bg-white/10 p-2 rounded text-white" aria-label="Toggle admin menu" data-testid="admin-menu-btn"><Menu /></button>
      <aside className={`${open ? "block" : "hidden"} lg:block fixed lg:sticky top-0 z-40 lg:w-64 w-72 h-screen overflow-y-auto bg-[#05070F] border-r border-white/10 p-5`}>
        <Link to="/"><Logo /></Link>
        <Link to="/" className="mt-6 mb-6 flex items-center gap-2 text-xs text-slate-500 hover:text-white"><ArrowLeft size={13} />Back to site</Link>
        <nav className="space-y-1">
          {NAV.filter((n) => isAdmin || !n[3]).map(([to, label, Icon]) => (
            <NavLink key={to} to={`/admin${to ? `/${to}` : ""}`} end onClick={() => setOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-white text-black" : "text-slate-300 hover:bg-white/5"}`} data-testid={`admin-nav-${to || "analytics"}`}><Icon size={16} />{label}
              {to === "messages" && unanswered > 0 && <span className="ml-auto min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#D4AF37] text-black text-xs font-bold flex items-center justify-center" title={`${unanswered} unanswered coaching question${unanswered > 1 ? "s" : ""}`} data-testid="admin-unanswered-badge">{unanswered}</span>}</NavLink>
          ))}
        </nav>
        <p className="mt-8 text-xs text-slate-600">Signed in as {user.email} ({user.role})</p>
      </aside>
      <main className="flex-1 p-5 sm:p-10 pt-16 lg:pt-10 min-w-0">
        <Routes>
          <Route index element={<Overview />} />
          {["videos", "categories", "courses", "posts", "events", "resources", "products", "programs", "landing_pages", "testimonials"].map((c) => <Route key={c} path={c} element={<CollectionManager coll={c} />} />)}
          <Route path="enrollments" element={<Simple path="/admin/enrollments" title="Enrollments" cols={[["user_email", "Member"], ["course_title", "Course"], ["source", "Source"], ["created_at", "Date", (r) => fmtDate(r.created_at)]]} />} />
          <Route path="subscribers" element={<Simple path="/admin/subscribers" title="Subscribers" cols={[["email", "Email"], ["name", "Name"], ["created_at", "Date", (r) => fmtDate(r.created_at)]]} />} />
          <Route path="messages" element={<Messages />} />
          {isAdmin && <>
            <Route path="members" element={<Members />} />
            <Route path="transactions" element={<Simple path="/admin/transactions" title="Payments" cols={[["created_at", "Date", (r) => fmtDate(r.created_at)], ["user_email", "Member"], ["label", "Item"], ["amount", "Amount", (r) => `$${r.amount?.toFixed(2)}`], ["payment_status", "Status"]]} />} />
            <Route path="pricing" element={<SettingsAdmin section="pricing" />} />
            <Route path="site" element={<SettingsAdmin section="site" />} />
          </>}
        </Routes>
      </main>
    </div>
  );
}
